import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import flatpickr from 'flatpickr';
import { French } from 'flatpickr/dist/l10n/fr';

// Included in application.css
// import 'flatpickr/dist/flatpickr.min.css';
import styled from 'styled-components';
import { parse } from 'date-fns';
import fr from 'date-fns/locale/fr';

import { DateTimeService } from '../services/DateTimeService';

const Container = styled.div`
  ${(props) => props.valid === true
    && `
      input {
        border-color: #51e3ae !important;
      }
    `}
  ${(props) => props.valid === false
    && `
      input {
        border-color: #f74656 !important;
        color: #f74656;
      }
  `}
`;

let flatpickrInstance;

const Flatpickr = (props) => {
  const {
    options, children, value = '', customInput, onChange, onClick, valid, onBlur, onOpen, dateFormat = 'yyyy-MM-dd', mode = 'single', locale = {}, defaultDate, ...restProps
  } = props;

  const pickerEl = useRef(null);

  useEffect(() => {
    const flatpickrOptions = {
      ...options,
      dateFormat,
      parseDate(date) {
        return parse(date, dateFormat, new Date(), { locale: fr });
      },
      formatDate(dateObj) {
        return DateTimeService.setDateLocale(dateObj, dateFormat);
      },
      mode,
      locale: {
        ...French,
        ...locale,
      },
      defaultDate,
    };
    if (onChange) {
      flatpickrOptions.onChange = onChange;
    }
    if (onClick) {
      flatpickrOptions.onClick = onClick;
    }
    if (onOpen) {
      flatpickrOptions.onOpen = onOpen;
    }
    flatpickrOptions.onClose = () => {
      if (pickerEl.current.blur) {
        pickerEl.current.blur();
      }
    };

    flatpickrInstance = flatpickr(pickerEl.current, flatpickrOptions);
    if (value) {
      flatpickrInstance.setDate(value, false);
    }

    return () => {
      if (flatpickrInstance) {
        flatpickrInstance.destroy();
        flatpickrInstance = null;
      }
    };
  }, []);

  useEffect(() => {
    if (value && flatpickrInstance) {
      flatpickrInstance.setDate(value, false);
    }
  }, [value]);

  const renderInput = () => {
    if (customInput) {
      return React.cloneElement(customInput, { ref: pickerEl, onBlur, ...restProps }, null);
    }
    return <input {...restProps} onBlur={onBlur} ref={pickerEl} />;
  };

  return (
    <Container valid={valid}>
      {options.wrap
        ? <div {...restProps} ref={pickerEl}>{children}</div>
        : renderInput()}
    </Container>
  );
};

Flatpickr.propTypes = {
  options: PropTypes.object, // see flatpickr docs for more info https://flatpickr.js.org/options/
  children: PropTypes.node,
  value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.array]),
  customInput: PropTypes.node,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  valid: PropTypes.bool,
  onBlur: PropTypes.func,
  onOpen: PropTypes.func,
  dateFormat: PropTypes.string,
  mode: PropTypes.string,
  locale: PropTypes.object,
  defaultDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.array]),
};

Flatpickr.defaultProps = {
  options: {},
  children: undefined,
  value: undefined,
  customInput: undefined,
  onChange: undefined,
  onClick: undefined,
  valid: undefined,
  onBlur: undefined,
  onOpen: undefined,
  dateFormat: undefined,
  mode: 'single',
  locale: {},
  defaultDate: null,
};

export { Flatpickr };
